import { useEffect, useMemo, useState } from 'react';
import {
  useApolloClient,
  useLazyQuery,
  useMutation,
} from '@apollo/client/react';
import * as R from 'ramda';

import WorkOrderStatusPill from '@atom/components/common/workOrderDetail/workOrderStatusPill/WorkOrderStatusPill';
import {
  GET_WORK_ORDER,
  GET_WORK_ORDERS,
  WORK_ORDER_UPDATE,
} from '@atom/graph/work';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import { useWorkValidations } from '@atom/hooks/useWorkValidations';
import { Button, Icon, IconButton, Menu, Modal, Snackbar } from '@atom/mui';
import { WarningIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { PolicyAction } from '@atom/types/policy';
import { Task } from '@atom/types/task';
import { TimeEntryWorkOrder } from '@atom/types/timeEntry';
import { WorkOrderDetailType } from '@atom/types/work';
import { hasAccess } from '@atom/utilities/accessUtilities';
import {
  currentUserRole,
  doesNotHaveRolePermissions,
  hasRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import {
  isOtherOpenAssetWorkCheckEnabled,
  isOwnWorkClosingEnabled,
} from '@atom/utilities/tenantFeatureUtilities';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';
import {
  getErrorCode,
  isNilOrEmpty,
} from '@atom/utilities/validationUtilities';
import {
  CLOSED_STATUS_ID,
  COMPLETED_STATUS_ID,
  getWorkOrderStatusColor,
  IN_REVIEW_STATUS_ID,
  workOrderStatusTypes,
} from '@atom/utilities/workOrderStatusUtilities';
import {
  getValidationsFromError,
  handleRequiredFieldErrorMessage,
} from '@atom/utilities/workValidationUtilities';

import ConfirmationModal from './ConfirmationModal';

import classes from './workOrderStatusEdit.module.css';

const { MenuItem } = Menu;

const styles = {
  warningIconButton: {
    padding: '0',
  },
};

interface Props {
  workOrder: WorkOrderDetailType | TimeEntryWorkOrder;
  createdById: string;
  styleType: '' | 'table' | 'detail' | 'detail-pane' | 'dashboard-map-list';
  refetch: () => void;
}

const WorkOrderStatusEdit = ({
  workOrder,
  createdById,
  refetch,
  styleType,
}: Props) => {
  const [{ view }, { updateSearchParams }] = useNavigation({
    view: SearchParamType.STRING,
  });

  const client = useApolloClient();

  const userProfile = useUserProfile();

  const preferences = usePreferences();
  const reopenClosedWorkEnabled =
    preferences?.workOrders?.reopenClosedWorkEnabled;
  const completedDateEnabled = preferences?.workOrders?.completedDateEnabled;
  const reopenOwnClosedWork = preferences?.workOrders?.reopenOwnClosedWork;

  const { setWorkValidations } = useWorkValidations();

  const [statusId, setStatusId] = useState<string>(workOrder.statusId);
  const [completionDate, setCompletionDate] = useState<Date>(
    workOrder.completionDate ? new Date(workOrder.completionDate) : new Date(),
  );
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [openError, setOpenError] = useState<boolean>(false);

  const [workWarning, setWorkWarning] = useState<boolean>(false);

  const [
    getFullWorkOrder,
    { data: fullWorkOrderData, loading: fullWorkOrderLoading },
  ] = useLazyQuery(GET_WORK_ORDER, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });

  const [updateWorkDetail, { loading: workUpdateLoading }] = useMutation(
    WORK_ORDER_UPDATE,
    {
      onCompleted: () => {
        setOpenConfirmation(false);
        setOpenError(false);
        refetch();
      },
    },
  );

  useEffect(() => {
    setStatusId(workOrder.statusId);
  }, [workOrder.statusId]);

  useEffect(() => {
    setCompletionDate(
      workOrder.completionDate
        ? new Date(workOrder.completionDate)
        : new Date(),
    );
  }, [workOrder.completionDate]);

  const fullWorkOrder = fullWorkOrderData?.workOrder;

  const setGWRRWorkWarning = async () => {
    const { data } = await client.query({
      query: GET_WORK_ORDERS,
      fetchPolicy: 'network-only',
      variables: {
        input: {
          assetId: workOrder?.inventoryAssetId,
          statusIds: [3, 4, 6],
        },
      },
    });

    const totalCount = data?.workOrders?.totalCount;

    if (totalCount > 1) {
      setWorkWarning(true);
    }
  };

  useEffect(() => {
    // For work detail views, if work has a root asset and the client is GWRR, check
    // to see if the asset has any other "open" work and show error accordingly.
    if (
      styleType === 'detail' &&
      !isNilOrEmpty(workOrder?.inventoryAssetId) &&
      isOtherOpenAssetWorkCheckEnabled
    ) {
      setGWRRWorkWarning();
    }
  }, [workOrder, styleType]);

  const hasIncompleteTasks = useMemo(() => {
    return !isNilOrEmpty(workOrder.tasks)
      ? R.pathOr([], ['tasks'], workOrder).reduce(
          (acc: boolean, task: Task): boolean => {
            return acc ? !task.isCompleted : acc;
          },
          true,
        )
      : false;
  }, [workOrder]);

  const hasPendingChanges = useMemo(() => {
    if (!fullWorkOrder) {
      return false;
    }

    return R.values(fullWorkOrder.assets).some(
      (asset: any) => asset.hasPendingChanges,
    );
  }, [fullWorkOrder]);

  // User can close their own work if the current client is Hennepin
  // and work is created by logged in user.
  const canCloseOwnWork =
    isOwnWorkClosingEnabled && userProfile?.userId === createdById;

  const canCloseWork = canCloseOwnWork
    ? hasRolePermissions(ROLE_SETS.INSPECTOR)
    : hasRolePermissions(ROLE_SETS.MANAGER);

  const isWorkStatusDisabled = (
    currentStatus: string,
    status: string,
  ): boolean => {
    if (!canCloseWork && status === CLOSED_STATUS_ID) {
      return true;
    }

    if (
      currentStatus !== COMPLETED_STATUS_ID &&
      currentStatus !== CLOSED_STATUS_ID &&
      currentStatus !== IN_REVIEW_STATUS_ID &&
      status === IN_REVIEW_STATUS_ID
    ) {
      return true;
    }

    return false;
  };

  const dropdownStatusTypes = canCloseWork
    ? workOrderStatusTypes
    : workOrderStatusTypes.slice(0, Number(CLOSED_STATUS_ID));

  // If the tenant preference for reopening own closed work is true and the work
  // was created by the currently logged in user, then Inspectors and above can reopen it.
  const canOpenClosedWork =
    reopenOwnClosedWork &&
    reopenClosedWorkEnabled &&
    userProfile.userId === createdById
      ? hasRolePermissions(ROLE_SETS.INSPECTOR)
      : // admin can always reopen
        hasRolePermissions(ROLE_SETS.ADMIN) ||
        // everyone else has to have the tenant preference
        (reopenClosedWorkEnabled &&
          // if the work is NOT created from a work template, allow org admins
          ((!workOrder.workTemplateId &&
            hasRolePermissions(ROLE_SETS.ORG_ADMIN)) ||
            // work created from a work template can only be reopened by someone with
            // the correct RBAC policy assigned to them.
            hasAccess(
              PolicyAction.REOPEN_CLOSED_WORK,
              workOrder.workTemplateFolderActions,
            )));

  const disablePopoverOwnWork = doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR);

  const disablePopoverWork =
    (workOrder.statusId === CLOSED_STATUS_ID && !canOpenClosedWork) ||
    doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR) ||
    (currentUserRole === 'Inspector' && statusId === COMPLETED_STATUS_ID);

  // If the tenant preference for reopening own closed work is true and the work
  // was created by the currently logged in user, then Inspectors and above can reopen it.
  const disablePopover =
    reopenOwnClosedWork &&
    reopenClosedWorkEnabled &&
    userProfile.userId === createdById
      ? disablePopoverOwnWork
      : disablePopoverWork;

  const updateWorkOrder = async (
    statusIdValue: string,
    completionDateValue: Date,
  ) => {
    const isReopening = workOrder?.statusId === CLOSED_STATUS_ID;

    const sendCompletionDate =
      completedDateEnabled &&
      !isReopening &&
      !!completionDateValue &&
      (statusIdValue === CLOSED_STATUS_ID ||
        statusIdValue === COMPLETED_STATUS_ID);

    try {
      await updateWorkDetail({
        variables: {
          workOrder: {
            id: workOrder.id,
            ...(statusIdValue ? { statusId: statusIdValue } : {}),
            ...(sendCompletionDate
              ? {
                  completionDate:
                    convertDateToMillisGMTMidday(completionDateValue),
                }
              : {}),
          },
        },
      });
    } catch (err) {
      const errorCode = getErrorCode(err);

      const allValidations = getValidationsFromError(err);

      if (errorCode === 422) {
        setWorkValidations({
          workOrderId: workOrder.id,
          taskValidations: allValidations.mappedTaskValidations,
          workValidations: allValidations.workInfoValidations,
        });
      }

      if (errorCode === 423) {
        setOpenError(true);
      }

      if (errorCode !== 423) {
        handleRequiredFieldErrorMessage({
          errorCode,
          allValidations,
          Snackbar,
          updateSearchParams,
          // The Team Portal timesheet (styleType 'table') reuses the `view`
          // search param for its own unrelated section state, so it can't
          // be used to infer whether a work-order tab navigation is valid.
          isPreview: styleType === 'table' || !view,
        });
      }

      setOpenConfirmation(false);
    }
  };

  const handleCompletionDateChange = (date?: Date) => {
    if (!date) {
      setCompletionDate(null);
    } else if (!Number.isNaN(date.valueOf())) {
      setCompletionDate(date);
    }
  };

  const updateStatus = async newStatus => {
    setStatusId(newStatus);

    const isCurrentStatusClosed = workOrder.statusId === CLOSED_STATUS_ID;
    const isNewStatusClosed = newStatus === CLOSED_STATUS_ID;
    const isNewStatusCompletedWithIncompleteTasks =
      newStatus === COMPLETED_STATUS_ID && hasIncompleteTasks;
    const isNewStatusCompletedWithDateOrInspectorSettings =
      newStatus === COMPLETED_STATUS_ID &&
      (completedDateEnabled || currentUserRole === 'Inspector');

    // If no specific case is meet, freely update work status.
    // Some cases here could be consolidated, however some require a specific order.
    switch (true) {
      case isCurrentStatusClosed: {
        return setOpenConfirmation(true);
      }
      case isNewStatusClosed: {
        getFullWorkOrder({
          variables: {
            id: workOrder.id,
          },
        });

        return setOpenConfirmation(true);
      }
      case isNewStatusCompletedWithIncompleteTasks:
      case isNewStatusCompletedWithDateOrInspectorSettings: {
        return setOpenConfirmation(true);
      }
      default:
        return updateWorkOrder(newStatus, completionDate);
    }
  };

  const onClose = () => {
    setOpenConfirmation(false);
    setOpenError(false);
    setStatusId(workOrder.statusId);
    setCompletionDate(
      workOrder.completionDate
        ? new Date(workOrder.completionDate)
        : new Date(),
    );
  };

  const loading = workUpdateLoading || fullWorkOrderLoading;

  return (
    <>
      <Menu
        icon={
          <WorkOrderStatusPill
            statusId={workOrder.statusId}
            className={styleType}
            data-cy="workOrderStatus"
          />
        }
        disabled={disablePopover}
        noIconButton
        dataCyLabel="workOrderStatusMenu"
      >
        {dropdownStatusTypes.map(status => {
          const background = getWorkOrderStatusColor(status.value);
          const isDisabled = isWorkStatusDisabled(
            workOrder.statusId,
            status.value,
          );

          const onClick = isDisabled
            ? () => {}
            : () => updateStatus(status.value);

          return (
            <MenuItem
              key={status.value}
              onClick={onClick}
              disabled={isDisabled}
              selected={workOrder.statusId === status.value}
              data-cy={`workOrderStatusOption-${status.value}`}
            >
              <div className={classes.optionContainer}>
                <div className={classes.optionTitle}>
                  <div
                    style={{
                      background,
                      opacity: isDisabled ? '50%' : '100%',
                    }}
                    className={classes.statusIndicator}
                  />
                  {status.label}
                </div>
                {status.value === CLOSED_STATUS_ID && workWarning && (
                  <IconButton
                    tooltip="Asset has other open work."
                    style={styles.warningIconButton}
                  >
                    <Icon color={colors.brand.orange} icon={WarningIcon} />
                  </IconButton>
                )}
              </div>
            </MenuItem>
          );
        })}
      </Menu>
      <ConfirmationModal
        open={openConfirmation}
        onClose={onClose}
        workOrder={workOrder}
        statusId={statusId}
        hasPendingChanges={hasPendingChanges}
        hasIncompleteTasks={hasIncompleteTasks}
        loading={loading}
        updateWorkOrder={updateWorkOrder}
        completionDate={completionDate}
        handleCompletionDateChange={handleCompletionDateChange}
      />
      <Modal
        title="Cannot Reopen Work"
        open={openError}
        onCancel={onClose}
        footer={
          <div className={classes.footer}>
            <div />
            <Button color="primary" variant="contained" onClick={onClose}>
              OK
            </Button>
          </div>
        }
      >
        Work cannot be reopened because it contains a sub item that was moved
        after this work was closed.
      </Modal>
    </>
  );
};

export default WorkOrderStatusEdit;
